import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { UserViewFragment } from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';

interface Props {
  /**
   * The user to display the role for.
   */
  user: UserViewFragment;
  /**
   * Set a custom element for this component.
   */
  as?: React.ElementType;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Only display the 'Owner' role.
   */
  showOwnerOnly?: boolean;
}

/**
 * Display a user's role in the current node
 *
 * @constructor
 * @author Jonathan Bridges
 */
const UserRoleForNode: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  as: Component = 'span',
  className,
  showOwnerOnly = false
}) => {
  const cx = useClassNameMapper();
  const {
    formatMessage,
    intl,
    loading: textLoading
  } = useTranslation(EndUserComponent.USER_ROLE_FOR_NODE);

  if (textLoading) {
    return null;
  }

  function displayUserRoles(): React.ReactElement {
    const userRoles = user?.roles?.edges;
    if (!userRoles) {
      return null;
    } else {
      const roleNameList = userRoles.map(role => role.node.name);
      return (
        <Component className={cx(className)}>
          {intl.formatList(roleNameList, { type: 'unit' })}
        </Component>
      );
    }
  }

  return showOwnerOnly ? (
    user.isOwner ? (
      <Component className={cx(className)}>{formatMessage('owner')}</Component>
    ) : null
  ) : (
    displayUserRoles()
  );
};

export default UserRoleForNode;
